import api from './index';
import { buildAdminApiRequest } from './admin';

export type ApplicationCredential = {
  id: string;
  application: string;
  country: string;
  user: string;
  password?: string;
  passwordRedacted?: boolean;
  createdAt: string | null;
  updatedAt: string | null;
};

export type ApplicationCredentialInput = {
  application: string;
  country: string;
  user: string;
  password: string;
};

type CredentialFilters = {
  application?: string;
  country?: string;
};

const adminApplicationCredentialsApi = api.injectEndpoints({
  endpoints: build => ({
    getApplicationCredentials: build.query<
      ApplicationCredential[],
      CredentialFilters | void
    >({
      query: filters => {
        const activeFilters = filters || {};

        return buildAdminApiRequest({
          method: 'GET',
          path: '/application-credentials',
          params: {
            ...(activeFilters.application
              ? { application: activeFilters.application }
              : {}),
            ...(activeFilters.country ? { country: activeFilters.country } : {}),
          },
        });
      },
      providesTags: result =>
        result
          ? [
              { type: 'adminApplicationCredentials', id: 'LIST' },
              ...result.map(item => ({
                type: 'adminApplicationCredentials' as const,
                id: item.id,
              })),
            ]
          : [{ type: 'adminApplicationCredentials', id: 'LIST' }],
    }),
    createApplicationCredential: build.mutation<
      ApplicationCredential,
      ApplicationCredentialInput
    >({
      query: body => buildAdminApiRequest({
        method: 'POST',
        path: '/application-credentials',
        body,
      }),
      invalidatesTags: [{ type: 'adminApplicationCredentials', id: 'LIST' }],
    }),
    updateApplicationCredential: build.mutation<
      ApplicationCredential,
      ApplicationCredentialInput & { id: string }
    >({
      query: ({ id, ...body }) => buildAdminApiRequest({
        method: 'PATCH',
        path: `/application-credentials/${id}`,
        body,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'adminApplicationCredentials', id: 'LIST' },
        { type: 'adminApplicationCredentials', id: arg.id },
      ],
    }),
    deleteApplicationCredential: build.mutation<{ id: string }, string>({
      query: id => buildAdminApiRequest({
        method: 'DELETE',
        path: `/application-credentials/${id}`,
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'adminApplicationCredentials', id: 'LIST' },
        { type: 'adminApplicationCredentials', id },
      ],
    }),
  }),
});

export const {
  useCreateApplicationCredentialMutation,
  useDeleteApplicationCredentialMutation,
  useGetApplicationCredentialsQuery,
  useUpdateApplicationCredentialMutation,
} = adminApplicationCredentialsApi;
